import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useAvailabilityData } from '../contexts/AvailabilityDataContext';
import { useAvailabilityUI } from '../contexts/AvailabilityUIContext';
import { availabilityAPI } from '../services/api';
import {
  CreateMemberAvailabilityRequest,
  MemberAvailabilityBlock,
  RemoveAvailabilityRangeParams,
  AvailabilityRangeDeleteResult,
} from '../types/availability';
import { format, parseISO, startOfWeek, startOfMonth, endOfMonth, addWeeks } from 'date-fns';
import { validateAndSanitizeDate, validateAndSanitizeTime, sanitizeText, validateEnum, SecurityError } from '../utils/security';
import { apiErrorMessage } from '../utils/apiError';

/** How far "every week going forward" reaches — mirrors the backend's 26-week
 *  materialization horizon for recurring availability. */
export const SERIES_HORIZON_WEEKS = 26;

/** What a delete from the edit/delete modal should remove. */
export type DeleteScope =
  | 'block'    // just the clicked (merged) block
  | 'day'      // every open slot on that date
  | 'series';  // that weekday + time window for every week going forward

/** Backend weekday convention: 0=Mon..6=Sun (JS getDay() is 0=Sun). */
export const toBackendWeekday = (isoDate: string): number => (parseISO(isoDate).getDay() + 6) % 7;

const hoursLabel = (slots: number): string => {
  const h = (slots * 15) / 60;
  const n = Number.isInteger(h) ? String(h) : h.toFixed(2).replace(/\.?0+$/, '');
  return `${n} hour${h === 1 ? '' : 's'}`;
};

/** Human summary of a bulk removal, e.g. "Removed 7.5 hours of availability across 3 days. 1 hour with bookings was kept." */
export const describeRangeResult = (r: AvailabilityRangeDeleteResult): string => {
  if (r.deleted === 0 && r.kept_booked === 0) return 'No open availability found in that range.';
  const parts: string[] = [];
  if (r.deleted > 0) {
    parts.push(`Removed ${hoursLabel(r.deleted)} of availability across ${r.dates_affected} day${r.dates_affected === 1 ? '' : 's'}.`);
  } else {
    parts.push('Nothing removed.');
  }
  if (r.kept_booked > 0) parts.push(`${hoursLabel(r.kept_booked)} with bookings was kept.`);
  return parts.join(' ');
};

interface UseAvailabilityCRUDReturn {
  // Data Operations
  fetchWeeklyAvailability: (date?: Date) => Promise<void>;
  fetchMonthlyAvailability: (date?: Date) => Promise<void>;
  createAvailability: (data: CreateMemberAvailabilityRequest) => Promise<void>;
  updateAvailability: (blockId: number, data: CreateMemberAvailabilityRequest) => Promise<void>;
  deleteAvailability: (blockId: number, scope?: DeleteScope) => Promise<void>;
  /** Bulk-remove open availability in a date range (booked slots + breaks are kept). */
  deleteAvailabilityRange: (params: RemoveAvailabilityRangeParams) => Promise<AvailabilityRangeDeleteResult>;
  
  // Interaction Operations
  startDragCreate: (dayIndex: number, timeIndex: number, date: Date) => void;
  updateDragCreate: (endTimeIndex: number) => void;
  finishDragCreate: () => void;
  cancelDragCreate: () => void;
  
  startClickCreate: (date: Date, prefilledData?: Partial<CreateMemberAvailabilityRequest>) => void;
  startEdit: (block: MemberAvailabilityBlock) => void;
  startDelete: (blockId: number) => void;
  
  // Modal Operations
  closeAllModals: () => void;
}

export const useAvailabilityCRUD = (): UseAvailabilityCRUDReturn => {
  const { state: dataState, dispatch: dataDispatch } = useAvailabilityData();
  const { state: uiState, dispatch: uiDispatch } = useAvailabilityUI();
  
  // Helper function for getting blocks at specific slot (30-min visual grid, midnight-to-midnight)
  // slotIndex 0 = 00:00, 1 = 00:30, ..., 16 = 08:00, etc.
  const getBlockAtSlot = useCallback((date: Date, slotIndex: number) => {
    if (!dataState.weeklyAvailability) return null;
    const dateStr = format(date, 'yyyy-MM-dd');
    const blocks = dataState.weeklyAvailability.availability_blocks.filter(block => block.date === dateStr);

    const slotHour = Math.floor(slotIndex / 2);
    const slotMinute = (slotIndex % 2) * 30;
    const slotTimeMinutes = slotHour * 60 + slotMinute;

    return blocks.find(block => {
      const [startH, startM] = block.start_time.split(':').map(Number);
      const [endH, endM] = block.end_time.split(':').map(Number);
      const blockStartMinutes = startH * 60 + startM;
      const blockEndMinutes = endH * 60 + endM;

      return slotTimeMinutes >= blockStartMinutes && slotTimeMinutes < blockEndMinutes;
    }) || null;
  }, [dataState.weeklyAvailability]);

  // Data Operations with Optimistic Updates
  const fetchWeeklyAvailability = useCallback(async (date?: Date) => {
    try {
      dataDispatch({ type: 'SET_LOADING', payload: true });
      dataDispatch({ type: 'SET_ERROR', payload: null });
      
      const targetDate = date || uiState.calendar.currentDate;
      const weekStart = startOfWeek(targetDate, { weekStartsOn: 1 });
      const weekStartStr = format(weekStart, 'yyyy-MM-dd');
      
      // Fetch weekly availability
      const weeklyData = await availabilityAPI.getMyWeeklyAvailability(weekStartStr);
      dataDispatch({ type: 'SET_WEEKLY_AVAILABILITY', payload: weeklyData });
      
      // Clinic hours for the week: one request, keyed by weekday (closed days absent).
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      let byWeekday: Record<string, { start_time: string; end_time: string }> = {};
      try {
        byWeekday = await availabilityAPI.getGlobalHours();
      } catch {
        // Non-fatal: the calendar still renders, just without the open-hours shading.
      }
      const globalHoursMap: Record<string, { start_time: string; end_time: string }> = {};
      for (let i = 0; i < 7; i += 1) {
        const day = new Date(weekStart);
        day.setDate(weekStart.getDate() + i);
        const hours = byWeekday[dayNames[day.getDay()]];
        if (hours) {
          globalHoursMap[format(day, 'yyyy-MM-dd')] = {
            start_time: hours.start_time.substring(0, 5),
            end_time: hours.end_time.substring(0, 5),
          };
        }
      }
      
      dataDispatch({ type: 'SET_GLOBAL_HOURS', payload: globalHoursMap });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch availability';
      dataDispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    } finally {
      dataDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [uiState.calendar.currentDate, dataDispatch]);

  // Fetch availability for an entire month by calling weekly API multiple times
  const fetchMonthlyAvailability = useCallback(async (date?: Date) => {
    try {
      dataDispatch({ type: 'SET_LOADING', payload: true });
      dataDispatch({ type: 'SET_ERROR', payload: null });

      const targetDate = date || uiState.calendar.currentDate;
      const monthStart = startOfMonth(targetDate);
      const monthEnd = endOfMonth(targetDate);

      // Get all week starts that overlap with this month
      const weekStarts: Date[] = [];
      let current = startOfWeek(monthStart, { weekStartsOn: 1 });
      while (current <= monthEnd) {
        weekStarts.push(current);
        current = addWeeks(current, 1);
      }

      // Fetch all weeks in parallel (max 5-6 calls)
      const weekResults = await Promise.all(
        weekStarts.map(ws => availabilityAPI.getMyWeeklyAvailability(format(ws, 'yyyy-MM-dd')))
      );

      // Merge and deduplicate blocks by ID
      const allBlocks: MemberAvailabilityBlock[] = [];
      const seenIds = new Set<number>();

      weekResults.forEach(weekData => {
        weekData.availability_blocks.forEach(block => {
          if (!seenIds.has(block.id)) {
            seenIds.add(block.id);
            allBlocks.push(block);
          }
        });
      });

      dataDispatch({ type: 'SET_MONTHLY_AVAILABILITY', payload: allBlocks });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch monthly availability';
      dataDispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    } finally {
      dataDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [uiState.calendar.currentDate, dataDispatch]);

  const createAvailability = useCallback(async (data: CreateMemberAvailabilityRequest) => {
    try {
      dataDispatch({ type: 'SET_LOADING', payload: true });
      
      // Validate and sanitize all inputs
      const validatedData = {
        date: validateAndSanitizeDate(data.date),
        start_time: validateAndSanitizeTime(data.start_time),
        end_time: validateAndSanitizeTime(data.end_time),
        recurrence_type: validateEnum(data.recurrence_type, ['one_time', 'recurring', 'recurring_biweekly', 'repeat_2_weeks', 'repeat_4_weeks']),
        recurrence_end_date: data.recurrence_end_date ? validateAndSanitizeDate(data.recurrence_end_date) : null,
        notes: sanitizeText(data.notes),
      };
      
      // Additional business logic validation
      const startTime = new Date(`1970-01-01T${validatedData.start_time}`);
      const endTime = new Date(`1970-01-01T${validatedData.end_time}`);
      
      if (startTime >= endTime) {
        throw new SecurityError('Start time must be before end time');
      }
      
      // Block availability creation in the past (date or time)
      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      if (validatedData.date < todayStr) {
        throw new SecurityError('Cannot create availability in the past');
      }
      if (validatedData.date === todayStr) {
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
        if (startMinutes < nowMinutes) {
          throw new SecurityError('Cannot create availability with a start time in the past');
        }
      }
      
      await availabilityAPI.createMyAvailability(validatedData);
      
      // Optimistic update - refresh data to get all blocks created by recurrence
      await fetchWeeklyAvailability();
      
      uiDispatch({ type: 'CLOSE_CREATE_MODAL' });
      toast.success('Availability created successfully');
      
    } catch (error) {
      let errorMessage = 'Failed to create availability';
      
      if (error instanceof SecurityError) {
        errorMessage = error.message; // Show validation errors to user
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      dataDispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    } finally {
      dataDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dataDispatch, uiDispatch, fetchWeeklyAvailability]);

  const updateAvailability = useCallback(async (blockId: number, data: CreateMemberAvailabilityRequest) => {
    try {
      dataDispatch({ type: 'SET_LOADING', payload: true });

      // Find ALL blocks in the merged range (not just the single block ID)
      const editingBlock = uiState.modals.edit.block;
      const allBlocks = dataState.weeklyAvailability?.availability_blocks || [];

      let blockIdsToDelete = [blockId];

      if (editingBlock) {
        const rangeBlocks = allBlocks.filter(b =>
          b.date === editingBlock.date &&
          b.start_time >= editingBlock.start_time &&
          b.end_time <= editingBlock.end_time &&
          b.is_active
        );
        if (rangeBlocks.length > 0) {
          blockIdsToDelete = rangeBlocks.map(b => b.id);
        }
      }

      // Delete all blocks in the old range
      for (const id of blockIdsToDelete) {
        try {
          await availabilityAPI.removeMyAvailabilityBlock(id);
        } catch (e: any) {
          const detail = apiErrorMessage(e, 'Cannot update — a slot in this range has an active booking.');
          throw new Error(detail);
        }
      }

      // Format data for backend
      const formattedData = {
        ...data,
        start_time: data.start_time.includes(':') ? data.start_time : `${data.start_time}:00`,
        end_time: data.end_time.includes(':') ? data.end_time : `${data.end_time}:00`,
        recurrence_end_date: data.recurrence_end_date && data.recurrence_end_date.trim() !== ''
          ? data.recurrence_end_date
          : null,
        notes: data.notes && data.notes.trim() !== '' ? data.notes : null,
      };

      await availabilityAPI.createMyAvailability(formattedData);

      // Refresh data
      await fetchWeeklyAvailability();

      uiDispatch({ type: 'CLOSE_EDIT_MODAL' });
      toast.success('Availability updated successfully');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update availability';
      dataDispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    } finally {
      dataDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dataDispatch, uiDispatch, uiState.modals, dataState.weeklyAvailability, fetchWeeklyAvailability]);

  const deleteAvailabilityRange = useCallback(async (params: RemoveAvailabilityRangeParams) => {
    try {
      dataDispatch({ type: 'SET_LOADING', payload: true });
      const result = await availabilityAPI.removeMyAvailabilityRange(params);
      if (uiState.calendar.viewMode === 'month') {
        await fetchMonthlyAvailability();
      } else {
        await fetchWeeklyAvailability();
      }
      return result;
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail
        || (error instanceof Error ? error.message : 'Failed to clear availability');
      dataDispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
      throw error;
    } finally {
      dataDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dataDispatch, uiState.calendar.viewMode, fetchWeeklyAvailability, fetchMonthlyAvailability]);

  const deleteAvailability = useCallback(async (blockId: number, scope: DeleteScope = 'block') => {
    // Day / series scopes go through the bulk endpoint, which keeps booked
    // slots — this is how a clinician clears the rest of a day around a client
    // booking, or drops a recurring pattern in one go.
    const scopedBlock = uiState.modals.edit.block;
    if (scope !== 'block' && scopedBlock) {
      const params: RemoveAvailabilityRangeParams = scope === 'day'
        ? { start_date: scopedBlock.date, end_date: scopedBlock.date }
        : {
            start_date: scopedBlock.date,
            end_date: format(addWeeks(parseISO(scopedBlock.date), SERIES_HORIZON_WEEKS), 'yyyy-MM-dd'),
            start_time: scopedBlock.start_time.substring(0, 5),
            end_time: scopedBlock.end_time.substring(0, 5),
            weekdays: [toBackendWeekday(scopedBlock.date)],
          };
      try {
        const result = await deleteAvailabilityRange(params);
        uiDispatch({ type: 'CLOSE_ALL_MODALS' });
        toast.success(describeRangeResult(result));
      } catch {
        // deleteAvailabilityRange already surfaced the error
      }
      return;
    }

    try {
      dataDispatch({ type: 'SET_LOADING', payload: true });

      // Find all blocks in the merged range by looking up the editing block's time span
      const editingBlock = uiState.modals.edit.block;
      const allBlocks = dataState.weeklyAvailability?.availability_blocks || [];

      let blockIdsToDelete = [blockId];

      if (editingBlock) {
        // Find all blocks on this date within the merged time range
        blockIdsToDelete = allBlocks
          .filter(b =>
            b.date === editingBlock.date &&
            b.start_time >= editingBlock.start_time &&
            b.end_time <= editingBlock.end_time &&
            b.is_active
          )
          .map(b => b.id);

        if (blockIdsToDelete.length === 0) {
          blockIdsToDelete = [blockId]; // fallback
        }
      }

      // Delete all blocks in the range, track failures
      let deleted = 0;
      let blocked = 0;
      for (const id of blockIdsToDelete) {
        try {
          await availabilityAPI.removeMyAvailabilityBlock(id);
          deleted++;
        } catch (e: any) {
          blocked++;
          console.warn(`Failed to delete block ${id}:`, e);
        }
      }

      await fetchWeeklyAvailability();
      uiDispatch({ type: 'CLOSE_ALL_MODALS' });

      if (blocked > 0 && deleted === 0) {
        toast.error('Cannot delete - this time slot has an active booking.');
      } else if (blocked > 0) {
        toast.success(`Availability partially deleted. ${blocked} slot(s) with active bookings were kept.`);
      } else {
        toast.success('Availability deleted successfully');
      }

    } catch (error) {
      await fetchWeeklyAvailability();

      const errorMessage = error instanceof Error ? error.message : 'Failed to delete availability';
      dataDispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    } finally {
      dataDispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dataDispatch, uiDispatch, uiState.modals, dataState.weeklyAvailability, fetchWeeklyAvailability, deleteAvailabilityRange]);

  // Interaction Operations
  const startDragCreate = useCallback((dayIndex: number, slotIndex: number, date: Date) => {
    // Check if there's already a block at this slot
    const existingBlock = getBlockAtSlot(date, slotIndex);
    if (existingBlock) {
      return; // Don't start drag on existing blocks
    }

    uiDispatch({
      type: 'START_DRAG',
      payload: { dayIndex, timeIndex: slotIndex, date },
    });
  }, [uiDispatch, getBlockAtSlot]);

  const updateDragCreate = useCallback((endTimeIndex: number) => {
    if (uiState.interaction.mode === 'dragging') {
      uiDispatch({
        type: 'UPDATE_DRAG',
        payload: { endTimeIndex },
      });
    }
  }, [uiState.interaction.mode, uiDispatch]);

  const finishDragCreate = useCallback(() => {
    if (uiState.interaction.mode === 'dragging') {
      uiDispatch({ type: 'END_DRAG' });
    }
  }, [uiState.interaction.mode, uiDispatch]);

  const cancelDragCreate = useCallback(() => {
    uiDispatch({ type: 'RESET_INTERACTION' });
  }, [uiDispatch]);

  const startClickCreate = useCallback((date: Date, prefilledData?: Partial<CreateMemberAvailabilityRequest>) => {
    uiDispatch({
      type: 'START_CREATE',
      payload: { date, prefilledData },
    });
  }, [uiDispatch]);

  const startEdit = useCallback((block: MemberAvailabilityBlock) => {
    uiDispatch({
      type: 'START_EDIT',
      payload: block,
    });
  }, [uiDispatch]);

  const startDelete = useCallback((blockId: number) => {
    uiDispatch({
      type: 'START_DELETE',
      payload: blockId,
    });
  }, [uiDispatch]);

  // Modal Operations
  const closeAllModals = useCallback(() => {
    uiDispatch({ type: 'CLOSE_ALL_MODALS' });
  }, [uiDispatch]);

  return {
    // Data Operations
    fetchWeeklyAvailability,
    fetchMonthlyAvailability,
    createAvailability,
    updateAvailability,
    deleteAvailability,
    deleteAvailabilityRange,

    // Interaction Operations
    startDragCreate,
    updateDragCreate,
    finishDragCreate,
    cancelDragCreate,
    startClickCreate,
    startEdit,
    startDelete,

    // Modal Operations
    closeAllModals,
  };
};

export default useAvailabilityCRUD;